import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Redirect } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import NavBar from '../../components/NavBar';
import * as api from './api';
import {
  agruparCursos, linkCadastro, nomeNaListaDeVendedores, podeAprovarSolicitacoes,
  podeSolicitarVinculacao, turmasParaSolicitar
} from './regras';
import { exportarParaExcel } from './exportarExcel';
import Cartoes from './Cartoes';
import BarraFiltros from './BarraFiltros';
import TabelaCursos from './TabelaCursos';
import Paginacao from './Paginacao';
import PainelCurso from './PainelCurso';
import ListaSolicitacoes from './ListaSolicitacoes';
import { DialogoAvaliar, DialogoProrrogar, DialogoSolicitar } from './Dialogos';
import './connect.css';

const POR_PAGINA = 50;
const FILTROS_VAZIOS = {
  categoriaStatus: [], vendedores: [], estados: [], instituicoes: [], cidades: [], cursos: [], anos: [], statusGrupo: [], siglas: []
};

function lerUsuario() {
  try {
    const sessao = JSON.parse(sessionStorage.getItem('usuarioLogado'));
    const usuario = sessao && sessao[0] ? { ...sessao[0] } : {};
    if (usuario.idUsuario) usuario.id = usuario.idUsuario;
    return usuario;
  } catch (e) {
    return {};
  }
}

export default function Connect() {
  const [usuario] = useState(lerUsuario);
  const permissao = usuario.permissao_relatorio || 'nenhum';
  const meuNome = nomeNaListaDeVendedores(usuario.id);
  const aprovador = podeAprovarSolicitacoes(usuario) && permissao !== 'ver';

  const [aba, setAba] = useState(meuNome ? 'meus' : 'todos');
  const [filtros, setFiltros] = useState(FILTROS_VAZIOS);
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [pagina, setPagina] = useState(1);
  const [linhas, setLinhas] = useState([]);
  const [paginacao, setPaginacao] = useState({ total: 0, totalPages: 0 });
  const [carregando, setCarregando] = useState(true);
  const [resumo, setResumo] = useState(null);
  const [carregandoResumo, setCarregandoResumo] = useState(false);
  const [opcoes, setOpcoes] = useState({});
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [cursoAberto, setCursoAberto] = useState(null);
  const [dialogo, setDialogo] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [versao, setVersao] = useState(0);
  const solicitacoesRef = useRef([]);

  const temToken = !!sessionStorage.getItem('token');

  // Filtros enviados à API. "Meus cursos" = filtro Vendedor com o nome do usuário.
  const parametros = useMemo(() => ({
    ...filtros,
    vendedores: aba === 'meus' && meuNome ? [meuNome] : filtros.vendedores,
    buscaGlobal: buscaAplicada
  }), [filtros, aba, meuNome, buscaAplicada]);

  // Busca com espera de 0,5 s, como na tela antiga
  useEffect(() => {
    const espera = setTimeout(() => { setBuscaAplicada(busca); setPagina(1); }, 500);
    return () => clearTimeout(espera);
  }, [busca]);

  useEffect(() => {
    document.title = 'Mapa de Vendas | Gestão Comercial';
    if (!temToken) toast.error('Erro de autenticação. Por favor, faça o login novamente.');
    api.buscarOpcoesFiltro()
      .then(dados => setOpcoes(dados || {}))
      .catch(() => toast.error('Não foi possível carregar as opções de filtro.'));
  }, [temToken]);

  // Página de cursos
  useEffect(() => {
    if (!temToken) { setCarregando(false); return undefined; }
    const cancelamento = api.novoCancelamento();
    setCarregando(true);
    api.buscarCursos({ ...parametros, page: pagina, limit: POR_PAGINA }, cancelamento.token)
      .then(dados => {
        setLinhas(dados.data || []);
        setPaginacao(dados.pagination || { total: 0, totalPages: 0 });
        if (dados.statusIndisponivel) {
          toast.warn('Não foi possível consultar o Bitrix agora: os status das turmas podem aparecer em branco. Atualize em instantes.', { autoClose: 8000 });
        }
        setCarregando(false);
      })
      .catch(erro => {
        if (api.foiCancelado(erro)) return;
        const descricao = erro.response && erro.response.data && erro.response.data.error_description;
        toast.error(descricao || 'Não foi possível carregar os dados.');
        setLinhas([]);
        setCarregando(false);
      });
    return () => cancelamento.cancel();
  }, [parametros, pagina, versao, temToken]);

  // Contadores do topo
  useEffect(() => {
    if (!temToken) return undefined;
    const cancelamento = api.novoCancelamento();
    setCarregandoResumo(true);
    api.buscarResumo(parametros, cancelamento.token)
      .then(dados => { setResumo(dados && dados.statusCounts ? dados : { totalInstituicoes: 0, statusCounts: {} }); setCarregandoResumo(false); })
      .catch(erro => {
        if (api.foiCancelado(erro)) return;
        setResumo({ erro: true, totalInstituicoes: 0, statusCounts: {} });
        setCarregandoResumo(false);
      });
    return () => cancelamento.cancel();
  }, [parametros, versao, temToken]);

  const carregarSolicitacoes = useCallback(() => {
    return api.buscarSolicitacoes()
      .then(dados => { solicitacoesRef.current = dados; setSolicitacoes(dados); })
      .catch(() => toast.error('Não foi possível carregar as solicitações.'));
  }, []);

  const responder = useCallback(async (solicitacao, aprovado, justificativa) => {
    setSalvando(true);
    try {
      await api.responderSolicitacao(solicitacao.id, aprovado, justificativa);
      toast.success(aprovado ? 'Solicitação aprovada!' : 'Solicitação rejeitada!');
      setDialogo(null);
      await carregarSolicitacoes();
      setVersao(v => v + 1);
    } catch (error) {
      toast.error('Não foi possível atualizar a solicitação.');
    }
    setSalvando(false);
  }, [carregarSolicitacoes]);

  // Solicitações: ao abrir e a cada 30 s. Aprovação automática de pendentes há 24 h, a cada 1 h
  // (como antes), sem fechar a janela que o usuário estiver usando.
  useEffect(() => {
    if (!temToken) return undefined;
    carregarSolicitacoes();
    const recarga = setInterval(carregarSolicitacoes, 30000);
    const automatica = setInterval(() => {
      const agora = new Date();
      solicitacoesRef.current
        .filter(s => s.status === 'pendente' && (agora - new Date(s.data_solicitacao)) / (1000 * 60 * 60) >= 24)
        .forEach(s => {
          api.responderSolicitacao(s.id, true, null)
            .then(() => { toast.success('Solicitação aprovada!'); carregarSolicitacoes(); })
            .catch(() => toast.error('Não foi possível atualizar a solicitação.'));
        });
    }, 60 * 60 * 1000);
    return () => { clearInterval(recarga); clearInterval(automatica); };
  }, [temToken, carregarSolicitacoes]);

  const cursos = useMemo(() => {
    const agrupados = agruparCursos(linhas, filtros.anos);
    // Mesma conferência da tela antiga para Livre/Vinculado
    if (filtros.statusGrupo.length > 0) {
      return agrupados.filter(c => filtros.statusGrupo.indexOf(c.vinculado ? 'Vinculado' : 'Livre') >= 0);
    }
    return agrupados;
  }, [linhas, filtros.anos, filtros.statusGrupo]);

  const alternarFiltro = useCallback((campo, valor) => {
    setFiltros(atual => {
      const lista = atual[campo] || [];
      const novaLista = lista.indexOf(valor) >= 0 ? lista.filter(v => v !== valor) : lista.concat([valor]);
      return { ...atual, [campo]: novaLista };
    });
    setPagina(1);
  }, []);

  const limparTudo = useCallback(() => {
    setFiltros(FILTROS_VAZIOS);
    setBusca('');
    setBuscaAplicada('');
    setPagina(1);
    toast.info('Todos os filtros foram limpos!');
  }, []);

  const trocarAba = (nova) => {
    setAba(nova);
    if (nova !== 'sol') setPagina(1);
  };

  const recarregarTudo = useCallback(() => {
    carregarSolicitacoes();
    setVersao(v => v + 1);
  }, [carregarSolicitacoes]);

  const orcar = (curso) => { window.open(linkCadastro(curso), '_blank'); };
  const abrirCurso = (curso, opcoesAbertura) => setCursoAberto({
    curso,
    relacionar: !!(opcoesAbertura && opcoesAbertura.relacionar),
    foco: (opcoesAbertura && opcoesAbertura.foco) || null
  });
  const fecharCurso = useCallback(() => setCursoAberto(null), []);
  const pedirSolicitacao = (curso, turmaFixa) => setDialogo({ tipo: 'solicitar', curso, turmaFixa });
  const pedirAvaliacao = (solicitacao) => setDialogo({ tipo: 'avaliar', solicitacao });
  const pedirProrrogacao = (solicitacao) => setDialogo({ tipo: 'prorrogar', solicitacao });
  const fecharDialogo = useCallback(() => { if (!salvando) setDialogo(null); }, [salvando]);

  const enviarSolicitacao = async ({ turma, contato, motivo }) => {
    const curso = dialogo.curso;
    const [ano, semestre] = String(turma || '').split('/');
    if (!ano) { toast.error('Por favor, selecione o ano de atendimento.'); return; }
    if (!semestre) { toast.error('Por favor, selecione o semestre de atendimento.'); return; }
    if (!contato.trim()) { toast.error('O contato é obrigatório.'); return; }
    if (!motivo.trim()) { toast.error('O motivo da solicitação é obrigatório.'); return; }
    if (!sessionStorage.getItem('token')) { toast.error('Erro de autenticação. Por favor, faça o login novamente.'); return; }
    if (!podeSolicitarVinculacao(usuario)) { toast.error('Você não tem permissão para realizar esta ação.'); return; }
    const turmaEscolhida = curso.turmas.find(t => t.Ano_Form === turma);

    setSalvando(true);
    try {
      await api.enviarSolicitacao({
        id_grupo: curso.id_representante || curso.id,
        id_vendedor_vinculado: (turmaEscolhida && turmaEscolhida.vendedor_id) || null,
        motivo,
        ano_atendimento: ano,
        semestre_atendimento: semestre,
        contato
      });
      setDialogo(null);
      recarregarTudo();
      toast.success('Solicitação enviada com sucesso!');
    } catch (error) {
      if (error.response) toast.error(`Erro: ${(error.response.data && error.response.data.message) || 'Não foi possível enviar a solicitação.'}`);
      else toast.error('Erro de conexão ao enviar a solicitação.');
    }
    setSalvando(false);
  };

  const confirmarProrrogacao = async () => {
    setSalvando(true);
    try {
      const resposta = await api.prorrogarPrioridade(dialogo.solicitacao.id);
      toast.success((resposta && resposta.message) || 'Prazo prorrogado com sucesso!');
      setDialogo(null);
      recarregarTudo();
    } catch (error) {
      toast.error((error.response && error.response.data && error.response.data.message) || 'Erro ao prorrogar prazo.');
    }
    setSalvando(false);
  };

  // Abre o curso de uma solicitação (a solicitação não traz a cidade; o painel acha pelo id)
  const abrirCursoDaSolicitacao = (s) => abrirCurso({ id: String(s.id_grupo), nomeintituicao: s.nome_instituicao, nomecurso: s.nome_curso }, {});

  if (!sessionStorage.getItem('usuarioLogado')) return <Redirect to="/login" />;

  // Paginação em cima e embaixo da tabela, com o mesmo estado
  const propsPaginacao = {
    pagina,
    totalPaginas: paginacao.totalPages || 0,
    total: paginacao.total || 0,
    porPagina: POR_PAGINA,
    carregando,
    onMudar: (n) => { setPagina(n); window.scrollTo(0, 0); }
  };

  const pendentes = solicitacoes.filter(s => s.status === 'pendente').length;
  const minhas = solicitacoes.filter(s => String(s.id_vendedor_solicitante) === String(usuario.id)).length;
  const mostraMinhas = !aprovador && (podeSolicitarVinculacao(usuario) || minhas > 0);
  const abas = [];
  if (meuNome) abas.push({ id: 'meus', nome: 'Meus cursos' });
  abas.push({ id: 'todos', nome: 'Todos os cursos' });
  if (aprovador) abas.push({ id: 'sol', nome: 'Solicitações pendentes', contador: pendentes, alerta: pendentes > 0 });
  else if (mostraMinhas) abas.push({ id: 'sol', nome: 'Minhas solicitações', contador: minhas });

  return (
    <React.Fragment>
      <ToastContainer position="bottom-left" autoClose={3000} />
      <NavBar nome={usuario && usuario.Nome} funcao={usuario && usuario.Funcao} />
      <div className="cx">
        <main className="cx-pagina">
          <div className="cx-cabecalho">
            <div>
              <h1>Connect</h1>
              <p>Cursos em prospecção, turmas de 2025 a 2030 e quem atende cada uma.</p>
            </div>
            <div className="cx-acoes-topo">
              <Link className="cx-link" to="/relatorio-vendas-antigo">Usar a tela antiga</Link>
              <button type="button" className="cx-btn" onClick={() => exportarParaExcel(parametros)}>Exportar Excel</button>
            </div>
          </div>

          <Cartoes resumo={resumo} carregando={carregandoResumo} filtros={filtros} onFiltrar={(campo, valor) => { alternarFiltro(campo, valor); if (aba === 'sol') trocarAba(meuNome ? 'meus' : 'todos'); }} />

          <div className="cx-abas" role="tablist">
            {abas.map(a => (
              <button type="button" role="tab" key={a.id} aria-selected={aba === a.id} onClick={() => trocarAba(a.id)}>
                {a.nome}
                {a.contador != null && <span className={`cx-contador${a.alerta && aba !== a.id ? ' cx-alerta' : ''}`}>{a.contador}</span>}
              </button>
            ))}
          </div>

          {aba === 'sol' ? (
            <ListaSolicitacoes
              solicitacoes={solicitacoes}
              usuario={usuario}
              modoAprovador={aprovador}
              podeAvaliar={aprovador}
              onAvaliar={pedirAvaliacao}
              onProrrogar={pedirProrrogacao}
              onAbrirCurso={abrirCursoDaSolicitacao}
            />
          ) : (
            <React.Fragment>
              <BarraFiltros
                busca={busca}
                onBusca={setBusca}
                filtros={filtros}
                opcoes={opcoes}
                ocultarVendedor={aba === 'meus'}
                onAlternar={alternarFiltro}
                onLimparTudo={limparTudo}
              />
              {aba === 'meus' && <p className="cx-dica">Em "Meus cursos", cada linha mostra só as suas turmas. Abra o curso para ver todas.</p>}
              {permissao !== 'ver' && (
                <div className="cx-legenda">
                  <span><i className="cx-sw cx-neg" />Em negociação</span>
                  <span><i className="cx-sw cx-fec" />Fechado</span>
                  <span><i className="cx-sw cx-per" />Perdido</span>
                  <span><i className="cx-sw cx-nm" />Etapa sem categoria</span>
                  <span><i className="cx-sw cx-vazio" />Sem negócio</span>
                  <span>Cada ano: metade esquerda = 1º semestre, direita = 2º</span>
                </div>
              )}
              <Paginacao posicao="topo" {...propsPaginacao} />
              <TabelaCursos
                cursos={cursos}
                carregando={carregando}
                usuario={usuario}
                permissao={permissao}
                solicitacoes={solicitacoes}
                onAbrir={abrirCurso}
                onOrcar={orcar}
                onSolicitar={pedirSolicitacao}
                onAvaliar={pedirAvaliacao}
                onLimparFiltros={limparTudo}
                rodape={<Paginacao posicao="rodape" {...propsPaginacao} />}
              />
            </React.Fragment>
          )}
        </main>

        {cursoAberto && (
          <PainelCurso
            key={cursoAberto.curso.id}
            cursoBase={cursoAberto.curso}
            abrirRelacionar={cursoAberto.relacionar}
            foco={cursoAberto.foco}
            usuario={usuario}
            permissao={permissao}
            solicitacoes={solicitacoes}
            onFechar={fecharCurso}
            onOrcar={orcar}
            onSolicitar={pedirSolicitacao}
            onAvaliar={pedirAvaliacao}
            onProrrogar={pedirProrrogacao}
            onAlterado={() => setVersao(v => v + 1)}
          />
        )}

        {dialogo && dialogo.tipo === 'solicitar' && (
          <DialogoSolicitar
            curso={dialogo.curso}
            turmaFixa={dialogo.turmaFixa}
            turmasPossiveis={turmasParaSolicitar(dialogo.curso, usuario, solicitacoes, permissao)}
            enviando={salvando}
            onEnviar={enviarSolicitacao}
            onFechar={fecharDialogo}
          />
        )}
        {dialogo && dialogo.tipo === 'avaliar' && (
          <DialogoAvaliar
            solicitacao={dialogo.solicitacao}
            salvando={salvando}
            onResponder={(aprovado, justificativa) => responder(dialogo.solicitacao, aprovado, justificativa)}
            onFechar={fecharDialogo}
          />
        )}
        {dialogo && dialogo.tipo === 'prorrogar' && (
          <DialogoProrrogar salvando={salvando} onConfirmar={confirmarProrrogacao} onFechar={fecharDialogo} />
        )}
      </div>
    </React.Fragment>
  );
}
